import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Tracks the single ProductCard instance whose video is currently allowed to
 * play (hover-triggered). Used so hovering a new card always pauses whatever
 * card was playing before it, keeping at most one video decoding at a time.
 * The mount-time sequence below does not use this tracker directly - it's
 * already sequential by construction, so only one of those plays at once too.
 * @type {ProductCard | null}
 */
let activeHoverVideoCard = null;

/**
 * How many of the first visible catalog products get sequential autoplay on mount.
 */
const MOUNT_SEQUENCE_LIMIT = 3;

/**
 * How long each card's video plays before advancing to the next, in ms.
 */
const MOUNT_SEQUENCE_STEP_MS = 4000;

/**
 * How long to wait, after any card first reports itself visible at mount, for
 * other cards' visibility observers to also report in before locking the
 * mount-sequence candidate list. IntersectionObserver callbacks land
 * asynchronously and not necessarily in catalog order, so this gives late
 * reporters a chance to be considered before the first three are chosen.
 */
const MOUNT_SEQUENCE_SETTLE_MS = 150;

/**
 * Cards (with catalog index) that reported themselves visible at mount and
 * are candidates for the sequence, collected before the settle timer fires.
 * @type {Array<{ card: ProductCard, index: number }>}
 */
let visibleMountSequenceCandidates = [];

/** Handle for the pending settle-and-finalize timer. */
let mountSequenceSettleTimer = null;

/**
 * Cards eligible for the mount-time sequence, in playback order, populated
 * once candidates are finalized.
 * @type {Array<ProductCard | undefined>}
 */
let mountSequenceRegistry = [];

/** Guards against starting the mount sequence more than once. */
let mountSequenceStarted = false;

/**
 * Records a card as visible-at-mount and a sequence candidate, then
 * (re)schedules finalization. Debounced so every card already visible on
 * page load gets a chance to report before the first three (by catalog
 * order) are locked in.
 * @param {ProductCard} card
 * @param {number} index
 */
function reportVisibleForMountSequence(card, index) {
  if (mountSequenceStarted) return;
  if (visibleMountSequenceCandidates.some((entry) => entry.card === card)) return;

  visibleMountSequenceCandidates.push({ card, index });

  if (mountSequenceSettleTimer != null) clearTimeout(mountSequenceSettleTimer);
  mountSequenceSettleTimer = setTimeout(() => {
    mountSequenceSettleTimer = null;
    finalizeMountSequenceCandidates();
  }, MOUNT_SEQUENCE_SETTLE_MS);
}

/**
 * Removes a card from visibility candidates and the sequence registry, e.g.
 * if it disconnects before the sequence reaches it (or before it settles).
 * @param {ProductCard} card
 */
function unregisterFromMountSequence(card) {
  visibleMountSequenceCandidates = visibleMountSequenceCandidates.filter((entry) => entry.card !== card);

  const index = mountSequenceRegistry.indexOf(card);
  if (index !== -1) mountSequenceRegistry[index] = undefined;
}

/**
 * Sorts visible candidates by catalog order, takes the first
 * MOUNT_SEQUENCE_LIMIT, populates the sequence registry, and starts playback.
 */
function finalizeMountSequenceCandidates() {
  const ordered = visibleMountSequenceCandidates
    .slice()
    .sort((a, b) => a.index - b.index)
    .slice(0, MOUNT_SEQUENCE_LIMIT);

  ordered.forEach((entry, i) => {
    mountSequenceRegistry[i] = entry.card;
  });

  startMountSequence();
}

/**
 * Plays each registered card's video in catalog order, one at a time, for
 * MOUNT_SEQUENCE_STEP_MS each, then pauses it before moving to the next.
 * Cards with no video, or that disconnected before their turn, are skipped
 * immediately without consuming any time.
 */
function startMountSequence() {
  if (mountSequenceStarted) return;
  mountSequenceStarted = true;

  const queue = mountSequenceRegistry.slice(0, MOUNT_SEQUENCE_LIMIT);

  const advance = (i) => {
    if (i >= queue.length) return;

    const card = queue[i];
    if (!card || !card.isConnected) {
      advance(i + 1);
      return;
    }

    card.playMountSequenceStep(() => advance(i + 1), MOUNT_SEQUENCE_STEP_MS);
  };

  advance(0);
}

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });

    this.#observeVisibilityForMountSequence();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);

    this.#mountVisibilityObserver?.disconnect();
    this.#mountVisibilityObserver = null;
    unregisterFromMountSequence(this);
    this.#clearMountSequenceTimer();

    // Release the shared "currently playing" slot if this card owned it.
    if (activeHoverVideoCard === this) {
      activeHoverVideoCard = null;
    }
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Finds the deferred-media element for the card gallery's video slide, if any.
   * Only the featured slide can be a video (see card-gallery.liquid), and only
   * when data-card-gallery-video is present on it.
   * @returns {HTMLElement | null}
   */
  get #deferredVideoMedia() {
    return this.refs.cardGallery?.querySelector('[data-card-gallery-video]') ?? null;
  }

  /**
   * This card's position within the product grid (0-based), used to determine
   * eligibility for the mount-time sequential autoplay. Returns -1 if the card
   * isn't inside a recognizable grid (e.g. quick-add preview), which excludes
   * it from the sequence.
   * @returns {number}
   */
  get #catalogIndex() {
    const grid = this.closest('[data-testid="product-grid"]');
    if (!grid) return -1;

    const item = this.closest('li') ?? this;
    return Array.from(grid.children).indexOf(item);
  }

  /** @type {ReturnType<typeof setTimeout> | null} */
  #mountSequenceTimer = null;

  /** @type {IntersectionObserver | null} */
  #mountVisibilityObserver = null;

  /**
   * Observes whether this card is actually visible at mount (not just present
   * in the DOM). If it is, and it has a video, reports itself as a candidate
   * for the mount-time sequential autoplay. Stops observing after the first
   * report either way - only initial visibility matters here.
   */
  #observeVisibilityForMountSequence() {
    if (!this.#deferredVideoMedia) return;
    if (mountSequenceStarted) return;

    const index = this.#catalogIndex;
    if (index < 0) return;

    this.#mountVisibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          reportVisibleForMountSequence(this, index);
          this.#mountVisibilityObserver?.disconnect();
          this.#mountVisibilityObserver = null;
          break;
        }
      },
      { threshold: 0 }
    );

    this.#mountVisibilityObserver.observe(this);
  }

  /**
   * Plays this card's video for its turn in the mount-time sequence, then
   * pauses it and calls onDone after durationMs. Public so the module-level
   * orchestrator can drive it without reaching into private state.
   * @param {() => void} onDone - Called after this card's step finishes.
   * @param {number} durationMs
   */
  playMountSequenceStep(onDone, durationMs) {
    this.#loadAndPlayVideo();

    this.#mountSequenceTimer = setTimeout(() => {
      this.#mountSequenceTimer = null;
      // Don't pause if the user is actively hovering this exact card -
      // let the hover interaction keep driving it instead of yanking it away.
      if (activeHoverVideoCard !== this) {
        this.#pauseCardVideo();
      }
      onDone();
    }, durationMs);
  }

  /**
   * Cancels this card's pending mount-sequence pause/advance timer, if any.
   * Called on disconnect so a removed card doesn't fire a stray callback.
   */
  #clearMountSequenceTimer() {
    if (this.#mountSequenceTimer != null) {
      clearTimeout(this.#mountSequenceTimer);
      this.#mountSequenceTimer = null;
    }
  }

  /**
   * Loads (if needed) and plays the card's video, without touching the
   * single-active-video tracker. Used for mount-time row-1 autoplay, which is
   * allowed to run alongside other cards' videos.
   */
  #loadAndPlayVideo() {
    const deferredMedia = this.#deferredVideoMedia;
    if (!deferredMedia) return;

    const existingVideo = deferredMedia.querySelector('video, iframe');
    if (existingVideo instanceof HTMLVideoElement) {
      existingVideo.play().catch(() => {});
      return;
    }
    if (existingVideo) return; // iframe (external video) already loaded, nothing more to do

    const posterButton = deferredMedia.querySelector('[ref="deferredMediaPlayButton"]');
    if (!(posterButton instanceof HTMLElement)) return;

    // The poster button lives inside card-gallery's wrapping <a ref="cardGalleryLink">,
    // which exists so that a real click anywhere on the gallery navigates to the PDP
    // (product-card.js's navigateToProduct explicitly defers to that native behavior).
    // We only want to trigger deferred-media's own click handling here (to swap the
    // poster for the real <video>), not a real navigation. A plain posterButton.click()
    // bubbles unprevented into that anchor and the browser navigates natively - that's
    // the bug. Calling preventDefault() on the event before dispatch cancels the
    // anchor's default action while still letting every listener along the path,
    // including deferred-media's, run exactly as they would for a real click.
    const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
    clickEvent.preventDefault();
    posterButton.dispatchEvent(clickEvent);
  }

  /**
   * Plays this card's video for hover, ensuring it's the only card playing.
   * Pauses whatever card was previously the active hover video, then loads/plays
   * this one and claims the active slot.
   */
  #playCardVideo = () => {
    if (!this.#deferredVideoMedia) return;

    if (activeHoverVideoCard && activeHoverVideoCard !== this) {
      activeHoverVideoCard.#pauseCardVideo();
    }

    this.#loadAndPlayVideo();
    activeHoverVideoCard = this;
  };

  /**
   * Pauses this card's video if it's playing, and releases the active slot
   * if this card currently holds it.
   */
  #pauseCardVideo = () => {
    const video = this.#deferredVideoMedia?.querySelector('video');
    if (video instanceof HTMLVideoElement) video.pause();

    if (activeHoverVideoCard === this) {
      activeHoverVideoCard = null;
    }
  };

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * On hover: if this card's featured slide is a video, plays it in place
   * (hover-scoped, pausing any other card's video first) and does NOT touch
   * the slideshow - swapping to slide 1 would just hide the now-playing video
   * behind the second image. If there's no video, falls back to the normal
   * swap-to-second-image behavior.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();

    if (this.#deferredVideoMedia) {
      this.#playCardVideo();
      return;
    }

    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * On hover-out: if this card's featured slide is a video, pauses it and
   * does NOT touch the slideshow (it was never moved off slide 0 to begin
   * with). If there's no video, falls back to the normal reset-to-variant/
   * previous-slide behavior.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    if (this.#deferredVideoMedia) {
      this.#pauseCardVideo();
      return;
    }

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}